/**
 * Which date pattern every screen writes its dates in.
 *
 * <p>
 * Three answers, in this order: what this person chose on this device, what their company chose,
 * and — when neither has — {@link defaultDateFormat}, which is what the product showed before this
 * setting existed. So a shop that never opens the settings screen sees no change at all.
 * </p>
 *
 * <p>
 * <strong>An external store rather than context, for the reason `shell/themeMode.ts` is one.</strong>
 * The value is read by `format/dates.ts` — a module of pure functions called from around forty
 * places, none of which is a component — and changed from two controls as far apart as two things
 * in this tree get: the settings screen and the account menu. A context would mean a provider and
 * forty call sites becoming hooks; lifting it to `App` would mean threading it through every screen.
 * `App` subscribes once with {@link useDateFormat}, and that subscription is the whole reason a
 * change repaints rather than sitting in storage until the next reload.
 * </p>
 *
 * <p>
 * <strong>The company's choice is cached here as well as being sent on every session.</strong> It
 * arrives with `GET /api/auth/me`, which resolves after the first paint — so without the cache a
 * reload of the booking register renders every date in the built-in format for a frame and then
 * flips. The cache is a display convenience and never authority: `App` overwrites it from the
 * session the moment that answers.
 * </p>
 */

/** Day-first with slashes. Mirrors `DateFormats.Default` in the Application layer. */
export const defaultDateFormat = 'DD/MM/YYYY';

/**
 * Every pattern that can be chosen, with what it renders — the client half of `DateFormats.All`.
 *
 * <p>
 * <strong>Duplicated from the server on purpose, and the two must be edited together.</strong> The
 * settings screen draws this list and the sample beside it before any request is made, and fetching
 * six constants to fill one dropdown would be a round trip for something that changes when that file
 * changes. The server is what refuses a value outside the set, so a pattern added only here is a 400
 * the moment somebody picks it — which is the safe direction for the two to disagree in.
 * </p>
 *
 * <p>
 * The samples are written out rather than rendered through dayjs at module load. They are what the
 * shop reads to decide, and a sample computed from today's date says "31 Dec" for one day in the
 * year and something else for the rest of it.
 * </p>
 */
export const dateFormats: readonly { value: string; sample: string }[] = [
  { value: defaultDateFormat, sample: '31/12/2026' },
  { value: 'DD-MM-YYYY', sample: '31-12-2026' },
  { value: 'MM/DD/YYYY', sample: '12/31/2026' },
  { value: 'YYYY-MM-DD', sample: '2026-12-31' },
  { value: 'DD MMM YYYY', sample: '31 Dec 2026' },
  { value: 'DD-MMM-YYYY', sample: '31-Dec-2026' },
];

/**
 * Whether a pattern is one this product knows how to render.
 *
 * <p>
 * Everything read back from storage or from the session goes through this. dayjs does not reject an
 * unrecognised pattern — it prints the parts it does not know literally — so a hand-edited storage
 * entry or a value stored against a pattern since retired would otherwise put nonsense where every
 * date on every screen used to be, with nothing to point at as the cause.
 * </p>
 */
export function isKnownDateFormat(value: string | null | undefined): value is string {
  return !!value && dateFormats.some((format) => format.value === value);
}

const USER_KEY = 'attirerent.dateFormat';
const COMPANY_KEY = 'attirerent.dateFormat.company';

const listeners = new Set<() => void>();

function readStored(key: string): string | null {
  try {
    const stored = localStorage.getItem(key);

    return isKnownDateFormat(stored) ? stored : null;
  } catch {
    // Private mode, or storage disabled. Falling back is correct and silent — the product still
    // renders dates, in the format below whichever one could not be read. On a phone there is no
    // `localStorage` at all and this is the path every read takes: the company's format still
    // arrives from the session, and the app restores a person's own override from its own storage
    // by calling `setUserDateFormat` at start-up.
    return null;
  }
}

/**
 * Read once at module load and kept in memory afterwards.
 *
 * <p>
 * `resolveDateFormat` is called during render, several times per row of every list in the product;
 * touching `localStorage` there would be a synchronous disk-backed read on the hottest path there
 * is. Storage is written through rather than read through, so the two cannot disagree.
 * </p>
 */
let userFormat: string | null = readStored(USER_KEY);
let companyFormat: string | null = readStored(COMPANY_KEY);

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // As above: the choice still applies to this sitting, it simply does not survive a reload —
    // which is a better outcome than refusing to change the format at all.
  }
}

function announce(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** The format in force: this person's choice, else their company's, else the built-in one. */
export function resolveDateFormat(): string {
  return userFormat ?? companyFormat ?? defaultDateFormat;
}

/** What this person has chosen for this device, or null when they are following the company. */
export function userDateFormat(): string | null {
  return userFormat;
}

/** What the company has chosen, or null when it has not. */
export function companyDateFormat(): string | null {
  return companyFormat;
}

/**
 * Sets this device's override, or clears it with null so the company's choice applies again.
 *
 * Anything unrecognised clears rather than being stored, so a stale value from an older build
 * cannot strand somebody on a pattern the product no longer renders.
 */
export function setUserDateFormat(format: string | null): void {
  const next = isKnownDateFormat(format) ? format : null;

  if (next === userFormat) {
    return;
  }

  userFormat = next;
  writeStored(USER_KEY, next);
  announce();
}

/**
 * Records the company's choice, from the session.
 *
 * <p>
 * Called by `App` whenever the signed-in user changes, including with null — a company that has
 * cleared its setting must fall back to the built-in format rather than keep the last one this
 * device happened to see cached.
 * </p>
 */
export function setCompanyDateFormat(format: string | null): void {
  const next = isKnownDateFormat(format) ? format : null;

  if (next === companyFormat) {
    return;
  }

  companyFormat = next;
  writeStored(COMPANY_KEY, next);
  announce();
}

/**
 * Notifies on every change to the format in force. The web wraps this in `useDateFormat`
 * (`format/useDateFormat.ts`); React stays out of the core so the mobile app can share the store.
 */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
